import { parseDate, formatDate, formatDateTime } from "../../utils/date";

import {
  FiBell,
  FiClipboard,
  FiCreditCard,
  FiHome,
  FiKey,
  FiMail,
  FiMessageSquare,
  FiStar,
  FiTag,
  FiUserCheck,
  FiUsers,
} from "react-icons/fi";

/**
 * `notification_for` values the backend writes (NotificationConstants, services) and the ones the
 * mobile Notification screen branches on. `group` drives the type filters; nothing is invented:
 * a value that is not listed falls back to the generic "Update" type.
 */
const TYPES = {
  "Customer lead": { label: "Lead", group: "leads", icon: FiClipboard, tone: "amber" },
  "Partner lead": { label: "Lead", group: "leads", icon: FiClipboard, tone: "amber" },
  "Customer lease": { label: "Lease", group: "leads", icon: FiKey, tone: "violet" },
  "Partner signup": { label: "Partner", group: "account", icon: FiUserCheck, tone: "green" },
  "Partner document upload": { label: "Partner", group: "account", icon: FiUserCheck, tone: "green" },
  PROPERTY_COMMENT: { label: "Property", group: "property", icon: FiHome, tone: "blue" },
  POST_COMMENT: { label: "Post", group: "post", icon: FiMessageSquare, tone: "teal" },
  PROMOTION_COMMENT: { label: "Promotion", group: "post", icon: FiTag, tone: "pink" },
  Invitation: { label: "Invitation", group: "network", icon: FiMail, tone: "violet" },
  USER: { label: "Network", group: "network", icon: FiUsers, tone: "blue" },
  FEEDBACK: { label: "Feedback", group: "account", icon: FiStar, tone: "amber" },
  TRIAL_EXPIRY_REMINDER: { label: "Subscription", group: "account", icon: FiCreditCard, tone: "red" },
  SUBSCRIPTION_EXPIRY_REMINDER: { label: "Subscription", group: "account", icon: FiCreditCard, tone: "red" },
};

const FALLBACK_TYPE = { label: "Update", group: "account", icon: FiBell, tone: "slate" };

export const getNotificationType = (notification) =>
  TYPES[notification?.notification_for] || FALLBACK_TYPE;

export const FILTER_GROUPS = [
  { key: "property", label: "Properties" },
  { key: "post", label: "Posts & Promotions" },
  { key: "leads", label: "Leads & Leases" },
  { key: "network", label: "Network" },
  { key: "account", label: "Account" },
];

const positiveId = (value) => {
  const n = Number(value);
  return Number.isFinite(n) && n > 0 ? n : null;
};

/**
 * Where a notification opens on web. Order follows the mobile screen (post, property, promotion,
 * catalog). Only routes that exist on web are returned; null means the row does not navigate.
 */
export const resolveNotificationTarget = (notification, role) => {
  const base = role === "PARTNER" ? "/partner" : role === "CUSTOMER" ? "/customer" : null;
  const postId = positiveId(notification.post_id);
  const propertyId = positiveId(notification.property_id);
  const promotionId = positiveId(notification.promotion_id);
  const catalogId = positiveId(notification.catalog_id);

  if (postId && base) return `${base}/posts/${postId}`;
  if (propertyId) return base ? `${base}/properties/${propertyId}` : `/property-details/${propertyId}`;
  if (promotionId && base) return `${base}/promotions/${promotionId}`;
  if (catalogId && role === "CUSTOMER") return `/customer/interior-design/${catalogId}`;

  if (role === "CUSTOMER") {
    if (notification.notification_for === "Customer lead") return "/customer/leads";
    if (notification.notification_for === "Customer lease") return "/customer/leases";
    if (notification.notification_for === "Invitation") return "/customer/invitations";
  }
  return null;
};

/** created_on arrives as "dd-MM-yyyy HH:mm:ss" (the mobile screen parses it the same way). */
export const parseCreatedOn = (value) => parseDate(value);

export const formatRelativeTime = (date, now = new Date()) => {
  if (!date) return "";
  const seconds = Math.max(0, Math.floor((now - date) / 1000));
  if (seconds < 60) return "Just now";
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${minutes} min ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours} hr ago`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days} day${days === 1 ? "" : "s"} ago`;
  return formatDate(date, { fallback: "" });
};

export const formatFullDate = (date) => formatDateTime(date, { fallback: "" });

const startOfDay = (date) => new Date(date.getFullYear(), date.getMonth(), date.getDate()).getTime();

export const getGroupLabel = (date, now = new Date()) => {
  if (!date) return "Earlier";
  const diffDays = Math.round((startOfDay(now) - startOfDay(date)) / 86400000);
  if (diffDays <= 0) return "Today";
  if (diffDays === 1) return "Yesterday";
  return "Earlier";
};
